import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  Grid,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';

const navItems = [
  'Products',
  'Apps and Services',
  'Learn',
  'For Business',
  'For Developers',
  'Support',
];

function HomePage() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: '#fff' }}>
      {/* Top banners */}
      <Box sx={{ bgcolor: '#333', py: 1, textAlign: 'center', fontSize: 14 }}>
        Ultimate crypto security for your devices
        <Button size="small" sx={{ color: '#fff', ml: 2, borderBottom: '1px solid #fff', borderRadius: 0, p: 0, minWidth: 0 }}>
          Get started →
        </Button>
      </Box>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ bgcolor: '#111', boxShadow: 'none', borderBottom: '1px solid #222' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ border: '2px solid #fff', px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 22, letterSpacing: 2, mr: 3 }}>
              FIRMWAREAPP
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {navItems.map((item) => (
                <Button key={item} sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textTransform: 'none' }}>{item}</Button>
              ))}
            </Box>
          </Box>
          {/* Right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#fff' }}><LanguageIcon /></IconButton>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton onClick={handleMenu} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} keepMounted>
                {navItems.map((item) => (
                  <MenuItem key={item} onClick={handleClose}>{item}</MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'inline-block', bgcolor: '#2d253f', color: '#cfc3e2', px: 2, py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 14, letterSpacing: 1, mb: 2 }}>
                FIRMWARE MANAGEMENT
              </Box>
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: 32, sm: 36, md: 48 }, lineHeight: 1.1 }}>
              Secure Your Crypto Transactions
            </Typography>
            <Typography variant="h6" sx={{ color: '#ccc', mb: 3, fontWeight: 400, fontSize: { xs: 15, sm: 16 } }}>
              Firmware Flex™ – the best user experience to manage and update your device firmware securely.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2, mb: 3 }}>
              <Button component={RouterLink} to="/recovery" variant="contained" size="large" sx={{ 
                bgcolor: '#ff6f1a', 
                color: '#fff', 
                fontWeight: 700, 
                px: { xs: 3, md: 4 }, 
                py: 1.5,
                fontSize: { xs: 16, md: 18 }, 
                borderRadius: 2, 
                boxShadow: 'none', 
                minWidth: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 },
                '&:hover': { bgcolor: '#ff8c42' } 
              }}>
                Get Started
              </Button>
              <Button component={RouterLink} to="/link-device" variant="outlined" size="large" sx={{ 
                color: '#fff', 
                borderColor: '#fff', 
                fontWeight: 700, 
                px: { xs: 3, md: 4 }, 
                py: 1.5,
                fontSize: { xs: 16, md: 18 }, 
                borderRadius: 2, 
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': { borderColor: '#ff6f1a', color: '#ff6f1a' } 
              }}>
                Wallet Link
              </Button>
            </Box>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#222', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                    <span role="img" aria-label="lock" style={{ fontSize: '14px' }}>🔒</span>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#fff' }}>web3 security</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#222', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                    <span role="img" aria-label="easy" style={{ fontSize: '14px' }}>👌</span>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#fff' }}>Ease of use</Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 220, sm: 260, md: 340 } }}>
              <Box 
                component="img"
                src="/images/wallet-security.png"
                alt="Secure Wallet"
                sx={{ 
                  width: { xs: '200px', sm: '240px', md: '300px' },
                  height: 'auto',
                  maxHeight: { xs: 200, sm: 240, md: 300 },
                  objectFit: 'contain',
                  borderRadius: 4,
                  p: 2,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.5)'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: '#181818', color: '#bbb', py: 4, mt: { xs: 10, md: 16 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ fontWeight: 700, fontSize: 22, letterSpacing: 2, mb: { xs: 2, md: 0 } }}>
                FIRMWAREAPP
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Privacy Policy</Button>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Terms of Service</Button>
              <Button sx={{ color: '#bbb', textTransform: 'none', fontSize: 16 }} href="#">Contact</Button>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
              <Typography variant="body2" sx={{ color: '#888' }}>
                © {new Date().getFullYear()} FirmwareApp. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage; 